import {
  buildDocumentNumber,
  DEFAULT_OFFERING_NUMBER_PREFIX,
  type DocumentYearMode,
} from "./documentNumberFormat.ts";

export const SERIAL_START_MIN = 1;
export const SERIAL_START_MAX = 99999999;

export type DocumentNumberingSettingsRow = {
  invoice_serial_start?: number | null;
  invoice_serial_start_year?: string | null;
  offering_serial_start?: number | null;
  offering_serial_start_year?: string | null;
};

export function normalizeSerialStart(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  if (n < SERIAL_START_MIN || n > SERIAL_START_MAX) return null;
  return n;
}

export function effectiveSerialStart(
  settings: DocumentNumberingSettingsRow,
  yearSuffix: string,
  kind: "invoice" | "offering",
): number | null {
  if (kind === "invoice") {
    const startYear = String(settings.invoice_serial_start_year ?? "").trim();
    if (startYear !== yearSuffix) return null;
    return normalizeSerialStart(settings.invoice_serial_start);
  }
  const startYear = String(settings.offering_serial_start_year ?? "").trim();
  if (startYear !== yearSuffix) return null;
  return normalizeSerialStart(settings.offering_serial_start);
}

/** Next serial given stored counter tail, observed max, and optional configured start floor. */
export function computeNextSerial(
  storedLast: number,
  observedMax: number,
  configuredStart: number | null,
): number {
  const floor = configuredStart != null ? configuredStart - 1 : 0;
  return Math.max(storedLast, observedMax, floor) + 1;
}

export function buildOfferingDisplayId(
  yearSuffix: string,
  serial: number,
  format?: {
    prefix?: string;
    yearMode?: DocumentYearMode;
    seqDigits?: number;
    includeMonth?: boolean;
    monthKey?: string;
    delimiter?: string;
  },
): string {
  const yy = String(yearSuffix ?? "").trim();
  const s = Math.max(SERIAL_START_MIN, Math.floor(Number(serial)) || SERIAL_START_MIN);
  return buildDocumentNumber({
    prefix: format?.prefix ?? DEFAULT_OFFERING_NUMBER_PREFIX,
    yearMode: format?.yearMode ?? "yy",
    yearKey: yy,
    serial: s,
    seqDigits: format?.seqDigits ?? 5,
    includeMonth: format?.includeMonth,
    monthKey: format?.monthKey,
    delimiter: format?.delimiter,
  });
}

/** True when at least one number was already issued for the year (not just a configured start). */
export function documentSerialCounterInUse(observedSerialMax: number): boolean {
  return observedSerialMax > 0;
}

/** `yearSuffix` is the period key: `26`, `2026`, `0`, or `26M09`. */
export function serialStartForCurrentYear(
  start: number | null | undefined,
  startYear: string | null | undefined,
  yearSuffix: string,
): number | null {
  const normalized = normalizeSerialStart(start);
  if (normalized == null) return null;
  if (String(startYear ?? "").trim() !== yearSuffix) return null;
  return normalized;
}

/** Last issued serial implied by observed documents and/or configured start for the year. */
export function displayLastIssuedSerial(
  observedMax: number,
  start: number | null | undefined,
  startYear: string | null | undefined,
  yearSuffix: string,
): number {
  const configuredStart = serialStartForCurrentYear(start, startYear, yearSuffix);
  const configuredLast = configuredStart != null ? configuredStart - 1 : 0;
  return Math.max(observedMax, configuredLast);
}
